import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ChevronDown, BellRing } from "lucide-react";
import { toast } from "sonner";

const STYLES = {
  completed: "bg-emerald-100 text-emerald-800",
  in_progress: "bg-sky-100 text-sky-800",
  scheduled: "bg-stone-100 text-stone-600",
  overdue: "bg-rose-100 text-rose-700",
  none: "bg-stone-50 text-stone-400 border border-dashed border-stone-200",
};
const LABELS = { completed: "Done", in_progress: "In progress", scheduled: "Scheduled", overdue: "Overdue", none: "Not scheduled" };

export const AuditCompletion = () => {
  const { user } = useAuth();
  const isAdmin = user.role === "corporate_admin";
  const [data, setData] = useState(null);
  const [openRows, setOpenRows] = useState({});
  const [threshold, setThreshold] = useState("");

  useEffect(() => {
    api.get("/dashboard/audit-completion").then((r) => setData(r.data)).catch(() => {});
    api.get("/settings/compliance-threshold").then((r) => setThreshold(String(r.data.threshold))).catch(() => {});
  }, []);

  const saveThreshold = async () => {
    try {
      await api.put("/settings/compliance-threshold", { threshold: Number(threshold) });
      toast.success(`Compliance alerts fire below ${threshold}%`);
    } catch {
      toast.error("Could not save threshold");
    }
  };

  if (!data || data.templates.length === 0) return null;

  return (
    <div className="bg-white border border-[#E7E5DF] rounded-xl p-6" data-testid="audit-completion">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-semibold text-stone-800">Audit Completion by Hotel</h3>
          <p className="text-[11px] text-stone-400 mt-0.5">Latest cycle of each audit — expand a row to see every hotel's status. <Link to="/progress" className="text-[#14532D] font-semibold hover:underline" data-testid="view-hotel-progress-link">View full hotel progress →</Link></p>
        </div>
        {isAdmin && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="threshold-button">
                <BellRing className="w-3.5 h-3.5 mr-1.5" />Alert below {threshold || "75"}%
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 space-y-2">
              <p className="text-xs font-semibold text-stone-600">Compliance alert threshold</p>
              <p className="text-[11px] text-stone-400">GM, Cluster Head and Admin are notified (and emailed) when a submitted audit scores below this.</p>
              <div className="flex gap-2">
                <Input data-testid="threshold-input" type="number" min={0} max={100} value={threshold} onChange={(e) => setThreshold(e.target.value)} className="h-8" />
                <Button size="sm" className="bg-[#14532D] hover:bg-[#0F2E1B]" data-testid="threshold-save" onClick={saveThreshold}>Save</Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      <div className="space-y-2">
        {data.templates.map((t) => (
          <div key={t.template_id} className="border border-stone-100 rounded-lg">
            <button data-testid={`completion-row-${t.template_id}`} className="w-full flex flex-wrap items-center gap-3 px-3 py-2.5 hover:bg-stone-50/60 rounded-lg"
              onClick={() => setOpenRows((o) => ({ ...o, [t.template_id]: !o[t.template_id] }))}>
              <span className="text-sm font-medium text-stone-800 text-left flex-1 min-w-[140px] truncate">{t.template}</span>
              <span className="flex items-center gap-2 flex-1 min-w-[160px]">
                <span className="flex-1 h-1.5 bg-stone-100 rounded-full">
                  <span className={`block h-1.5 rounded-full ${t.completed_pct >= 100 ? "bg-emerald-500" : t.completed_pct >= 50 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${t.completed_pct}%` }} />
                </span>
                <span className="font-mono text-xs font-semibold text-stone-700 w-20 text-right">{t.counts.completed}/{t.total} done</span>
              </span>
              <span className="flex items-center gap-1.5 text-[10px] font-semibold shrink-0">
                {t.counts.overdue > 0 && <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">{t.counts.overdue} overdue</span>}
                {t.counts.in_progress > 0 && <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-800">{t.counts.in_progress} running</span>}
                <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${openRows[t.template_id] ? "rotate-180" : ""}`} />
              </span>
            </button>
            {openRows[t.template_id] && (
              <div className="px-3 pb-3 flex flex-wrap gap-2" data-testid={`completion-detail-${t.template_id}`}>
                {t.hotels.map((h) => (
                  <span key={h.hotel} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium ${STYLES[h.status]}`} title={`${LABELS[h.status]}${h.date ? ` · ${h.date}` : ""}`}>
                    {h.hotel}
                    <span className="font-mono font-bold">{h.status === "completed" && h.score != null ? `${h.score}%` : LABELS[h.status]}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
